import {
  withLoading,
  useTranslator,
  calculateAge,
  withMobileAppMode,
  callNativeMobileApp,
  formatCalories,
} from '@front-activerse/common-components';
import { DietIdEnum } from '@front-activerse/common-components';
import { Container, Row, Col, Button, Alert } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import Layout from '../Layout/Layout';
import React from 'react';
import UniversalPlaceholder from '../Layout/UniversalPlaceholder';
import TitleBar from '../Layout/TitleBar';
import { dietSettings } from '../DietSettings/types';
import createFormikInitialValues from '../DietSettings/helpers/createFormikInitialValues';

interface ITrackerSettingsContainer {
  dietSettingsData: dietSettings;
  dietTypes: {
    id: number;
    value: DietIdEnum;
    optionTitle: string;
    optionDescription: string;
    macro: {
      protein: number;
      fat: number;
      carbs: number;
    };
  }[];
  dietLevels: {
    id: number;
    value: DietIdEnum;
    optionTitle: string;
    optionDescription: string;
    macro?: {
      protein: number;
      fat: number;
      carbs: number;
    };
  }[];
  mobileAppMode: boolean;
}

const TrackerSettingsContainer: React.FC<ITrackerSettingsContainer> = ({
  dietSettingsData,
  dietTypes,
  dietLevels,
  mobileAppMode,
}) => {
  const {
    weightValue,
    weightUnit,
    dietMode,
    goalWeightValue,
    dietName,
    activityLevel,
    spontaneousActivityLevel,
    sex,
    heightValue,
    heightUnit,
    dateOfBirth,
    baseCalories,
    macronutrientRatio,
    preferredMacronutrientRatio,
  } = createFormikInitialValues(dietSettingsData, dietTypes, dietLevels);
  const navigate = useNavigate();

  const { t } = useTranslator();

  const decimalSeparator = () => {
    const n = 1.1;
    const separator = n.toLocaleString().substring(1, 2);
    return separator;
  };

  const handleClick = (url: string) => {
    if (mobileAppMode) {
      callNativeMobileApp({
        action: 'open_modal',
        payload: {
          path: url,
          modal_variant: 'always_close_btn',
        },
      });
    } else {
      navigate(url, {
        state: {
          from: {
            pathname: url,
          },
        },
      });
    }
  };

  let macronutrientRatioToUse = macronutrientRatio;

  if (
    preferredMacronutrientRatio.protein !== 0 &&
    preferredMacronutrientRatio.fat !== 0 &&
    preferredMacronutrientRatio.carbohydrate !== 0
  ) {
    macronutrientRatioToUse = preferredMacronutrientRatio;
  }

  return (
    <Layout>
      <TitleBar showBackBtn={true}>{t('trackerSettings.navbarTitle')}</TitleBar>
      <section className="page-template-tracker-settings py-md-4 full-height3">
        <Container>
          <Row>
            <Col xl={{ size: 10, offset: 1 }}>
              <div className="page-container">
                <div className="tracker-container">
                  <h2 className="title-small semi-bold mb-s text-center">
                    {t('calorieNeeds.title')}
                  </h2>
                  <p className="description text-center">{t('calorieNeeds.subtitle')}</p>

                  <div className="calorie-need-box">
                    <h3 className="mb-xxs">
                      {baseCalories > 0 ? `${formatCalories(baseCalories)} kcal` : '-- kcal'}
                    </h3>

                    {baseCalories > 0 && (
                      <p className="mb-0 description-small">
                        {t('calorieNeeds.basis.description')}
                      </p>
                    )}
                  </div>

                  {baseCalories === 0 && (
                    <Alert color="warning" className="d-block" fade={false}>
                      <p className="small">{t('calorieNeeds.update.content')}</p>
                      <div className="text-center mt-l">
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => {
                            handleClick('/diet-settings/all');
                          }}
                        >
                          {t('calorieNeeds.error.button')}
                        </Button>
                      </div>
                    </Alert>
                  )}
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col xs="12" md={{ size: 10, offset: 1 }}>
              {/* macro settings */}
              <div className="tracker-settings-box-container">
                <h2 className="title-medium semi-bold mb-s">
                  {t('trackerSettings.macro.sectionTitle')}
                </h2>
                <div className="tracker-settings-box">
                  {dietName ? (
                    <>
                      <div className="px-m">
                        <div className="macro-progress-bar">
                          <div
                            className="macro-progress-bar-item protein"
                            style={{ width: `${macronutrientRatioToUse?.protein}%` }}
                          />
                          <div
                            className="macro-progress-bar-item fat"
                            style={{ width: `${macronutrientRatioToUse?.fat}%` }}
                          />
                          <div
                            className="macro-progress-bar-item carbs"
                            style={{ width: `${macronutrientRatioToUse?.carbohydrate}%` }}
                          />
                        </div>

                        <Row>
                          <Col>
                            <div className="d-flex align-items-start">
                              <div className="bullet protein"></div>
                              <div>
                                <span className="description small">
                                  {t('settings.dietType.macros.proteins')}
                                </span>
                                <br />
                                <span className="semi-bold">
                                  {macronutrientRatioToUse?.protein}%
                                </span>
                              </div>
                            </div>
                          </Col>
                          <Col>
                            <div className="d-flex align-items-start">
                              <div className="bullet fat"></div>
                              <div>
                                <span className="description small">
                                  {t('settings.dietType.macros.fats')}
                                </span>
                                <br />
                                <span className="semi-bold">{macronutrientRatioToUse?.fat}%</span>
                              </div>
                            </div>
                          </Col>
                          <Col>
                            <div className="d-flex align-items-start">
                              <div className="bullet carbs"></div>
                              <div>
                                <span className="description small">
                                  {t('settings.dietType.macros.carbs')}
                                </span>
                                <br />
                                <span className="semi-bold">
                                  {macronutrientRatioToUse?.carbohydrate}%
                                </span>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>

                      {baseCalories > 0 && (
                        <div className="text-center mt-xl px-4">
                          <Row>
                            <Col md={{ size: 6, offset: 3 }}>
                              <Button
                                color="outline-secondary"
                                size="sm"
                                className="mx-auto w-100"
                                onClick={() => {
                                  handleClick(`/tracker/settings/macro`);
                                }}
                              >
                                {t('settings.nutrition.changeButton')}
                              </Button>
                            </Col>
                          </Row>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="px-m small text-text-variant-color mb-0 mt-m">
                      {t('settings.nutrition.noMealPlan')}
                    </p>
                  )}
                </div>
              </div>

              {/* diet settings */}
              <div className="tracker-settings-box-container">
                <h2 className="title-medium semi-bold mb-s">
                  {t('trackerSettings.nutrition.sectionTitle')}
                </h2>
                <div className="tracker-settings-box">
                  {dietName ? (
                    <>
                      <div className="pt-l px-m">
                        <p className="semi-bold">{dietName}</p>

                        <Row>
                          <Col>
                            <div className="d-flex align-items-start">
                              <div>
                                <span className="description small">
                                  {t('settings.dietType.macros.proteins')}
                                </span>
                                <br />
                                <span className="semi-bold">{macronutrientRatio?.protein}%</span>
                              </div>
                            </div>
                          </Col>
                          <Col>
                            <div className="d-flex align-items-start">
                              <div>
                                <span className="description small">
                                  {t('settings.dietType.macros.fats')}
                                </span>
                                <br />
                                <span className="semi-bold">{macronutrientRatio?.fat}%</span>
                              </div>
                            </div>
                          </Col>
                          <Col>
                            <div className="d-flex align-items-start">
                              <div>
                                <span className="description small">
                                  {t('settings.dietType.macros.carbs')}
                                </span>
                                <br />
                                <span className="semi-bold">
                                  {macronutrientRatio?.carbohydrate}%
                                </span>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>

                      {baseCalories > 0 && (
                        <div className="text-center mt-xl px-4">
                          <Row>
                            <Col md={{ size: 6, offset: 3 }}>
                              <Button
                                color="outline-secondary"
                                size="sm"
                                className="mx-auto w-100"
                                onClick={() => {
                                  handleClick('/diet-settings/nutritionSettings/diet-type');
                                }}
                              >
                                {t('settings.nutrition.changeButton')}
                              </Button>
                            </Col>
                          </Row>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="px-m small text-text-variant-color mb-0 mt-m">
                      {t('settings.nutrition.noMealPlan')}
                    </p>
                  )}
                </div>
              </div>

              {/* goal settings */}
              <div className="tracker-settings-box-container">
                <h2 className="title-medium semi-bold mb-s">
                  {t('trackerSettings.goalSettings.sectionTitle')}
                </h2>
                <div className="tracker-settings-box">
                  <dl>
                    <dt>{t('settings.goalSettings.list.currentWeight')}</dt>
                    <dd>
                      {weightValue ? (
                        <>
                          {String(weightValue).replace('.', decimalSeparator())} {weightUnit}
                        </>
                      ) : (
                        '-'
                      )}
                    </dd>
                  </dl>

                  <dl>
                    <dt>{t('settings.goalSettings.list.goal')}</dt>
                    <dd>
                      {dietMode ? <>{t(`app.goalSettings.summary.list.goal.${dietMode}`)}</> : '-'}
                    </dd>
                  </dl>

                  {dietMode !== 'stabilization' && (
                    <dl>
                      <dt>{t('settings.goalSettings.list.targetWeight')}</dt>
                      <dd>
                        {goalWeightValue ? (
                          <>
                            {String(goalWeightValue).replace('.', decimalSeparator())} {weightUnit}
                          </>
                        ) : (
                          '-'
                        )}
                      </dd>
                    </dl>
                  )}

                  <dl>
                    <dt>{t('settings.goalSettings.list.spontaneousActivity')}</dt>
                    <dd>
                      {spontaneousActivityLevel ? (
                        <>
                          {t(
                            `app.goalSettings.summary.list.spontaneousActivity.${spontaneousActivityLevel}`
                          )}
                        </>
                      ) : (
                        '-'
                      )}
                    </dd>
                  </dl>

                  <dl>
                    <dt>{t('settings.goalSettings.list.activity')}</dt>
                    <dd>
                      {activityLevel ? (
                        <>{t(`app.goalSettings.summary.list.activity.${activityLevel}`)}</>
                      ) : (
                        '-'
                      )}
                    </dd>
                  </dl>

                  {baseCalories > 0 && (
                    <div className="text-center mt-s mb-s">
                      <span className="text-center description">
                        {t('settings.goalSettings.calculatedCalories')}{' '}
                        {formatCalories(baseCalories)} kcal
                      </span>
                    </div>
                  )}

                  {baseCalories > 0 && dietMode ? (
                    <div className="text-center mt-xl px-4">
                      <Row>
                        <Col md={{ size: 6, offset: 3 }}>
                          <Button
                            color="outline-secondary"
                            size="sm"
                            className="mx-auto w-100"
                            onClick={() => {
                              handleClick('/diet-settings/goalSettings/weight');
                            }}
                          >
                            {t('app.settings.goalSettings.editButton')}
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  ) : (
                    <div></div>
                  )}
                </div>
              </div>

              {/* biometric data settings */}
              <div className="tracker-settings-box-container mb-xl">
                <h2 className="title-medium semi-bold mb-s">
                  {t('trackerSettings.biometeric.sectionTitle')}
                </h2>
                <div className="tracker-settings-box mb-0">
                  <dl>
                    <dt>{t('settings.aboutYou.list.gender')}</dt>
                    <dd>{t(`biometric.summary.list.gender.${sex}`)}</dd>
                  </dl>

                  <dl>
                    <dt>{t('settings.aboutYou.list.age')}</dt>
                    <dd>
                      {calculateAge(dateOfBirth) ? (
                        <>
                          {calculateAge(dateOfBirth)} {t('settings.aboutYou.list.age.unit')}
                        </>
                      ) : (
                        '-'
                      )}
                    </dd>
                  </dl>

                  <dl>
                    <dt>{t('settings.aboutYou.list.height')}</dt>
                    <dd>
                      {heightValue ? (
                        <>
                          {Math.floor(heightValue)} {heightUnit}
                        </>
                      ) : (
                        '-'
                      )}
                    </dd>
                  </dl>

                  {baseCalories > 0 ? (
                    <div className="text-center mt-xl px-4">
                      <Row>
                        <Col md={{ size: 6, offset: 3 }}>
                          <Button
                            color="outline-secondary"
                            size="sm"
                            className="mx-auto w-100"
                            onClick={() => {
                              handleClick('/diet-settings/biometric/sex');
                            }}
                          >
                            {t('settings.aboutYou.editButton')}
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  ) : (
                    <div></div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </Layout>
  );
};

const Placeholder = () => <UniversalPlaceholder titleBar="trackerSettings.navbarTitle" />;

export default withLoading(withMobileAppMode(TrackerSettingsContainer), Placeholder);
